<template>
  <div>
    <h2 class="report-title">
      <i class="bi bi-bar-chart-fill"></i>
      {{  reportTitle }}
    </h2>
    <b-tabs card class="custom-tabs" nav-class="mb-3" v-model="activeTabIndex">
      <!-- Add the new Booking Revenue Report tab -->
      <b-tab title="Booking Summary">
        <BookingSummaryReport :hotelOptions="hotelOptions" :statusOptions="statusOptions" :paymentStatusOptions="paymentStatusOptions"/>
      </b-tab>
      <b-tab title="Hotel-wise Bookings">
        <HotelWiseBookingReport />
      </b-tab>
      <b-tab title="Booking Revenue">
        <BookingRevenueReport :hotelOptions="hotelOptions" :paymentStatusOptions="paymentStatusOptions"/>
      </b-tab>
      <b-tab title="Room Popularity">
        <RoomTypePopularityReport :hotelOptions="hotelOptions"/>
      </b-tab>
      <b-tab title="Guests and Pre-checkin">
        <GuestReport :hotelOptions="hotelOptions" :guests="guests" :totalGuests="total_guests" :totalBookings="total_bookings" @get-guest-list="fetchGuests"/>
      </b-tab>
      <b-tab title="Check Ins">
        <CheckInReport :hotelOptions="hotelOptions" :paymentStatusOptions="paymentStatusOptions" :dailySummary= "dailySummary" @get-daily-summary="fetchDailySummary"/>
      </b-tab>
      <b-tab title="Check Outs">
        <CheckOutReport :hotelOptions="hotelOptions" :paymentStatusOptions="paymentStatusOptions" :dailySummary= "dailySummary" @get-daily-summary="fetchDailySummary"/>
      </b-tab>
      <b-tab title="Coupon Usage">
        <CouponUsageReport :hotelOptions="hotelOptions"/>
      </b-tab>
      <b-tab title="Occupancy">
        <OccupancyReport 
          :data="occupancyReportData"
          :fields="occupancyFields"
          :hotelOptions="hotelOptions"
          @get-occupancy-list="fetchOccupancy"/>
      </b-tab>
      <b-tab title="Revenue">
        <div class="section">
          <b-table :items="revenueTable" :fields="revenueFields" striped hover small responsive>
            <template #cell(revenue_per_room)="data">
              <b-table :items="data.item.revenue_per_room" :fields="roomFields" small bordered
                class="mb-2 inner-table" />
            </template>
            <template #cell(revenue_per_type)="data">
              <b-table :items="data.item.revenue_per_type" :fields="typeFields" small bordered
                class="mb-2 inner-table" />
            </template>
          </b-table>
        </div>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import axios from '@/axios';
import { request } from '@/Util/Request';
import BookingRevenueReport from './BookingRevenueReport.vue';
import CheckInReport from './CheckInReport.vue';
import CheckOutReport from './CheckOutReport.vue';
import GuestReport from './GuestReport.vue';
import BookingSummaryReport from '../hotelReports/BookingSummaryReport.vue';
import HotelWiseBookingReport from '../hotelReports/HotelWiseBookingReport.vue';
import RoomTypePopularityReport from '../hotelReports/RoomTypePopularityReport.vue';
import CouponUsageReport from '../hotelReports/CouponUsageReport.vue';
import OccupancyReport from './OccupancyReport.vue';
const PAYMENT_STATUS_OPTIONS = [
  { value: null, text: 'Select' },
  { value: 'pending', text: 'Pending' },
  { value: 'paid', text: 'Paid' },
  { value: 'cancelled', text: 'Cancelled' }
];

export default {
  name: 'ReportsIndex',
  components: {
    BookingRevenueReport,
    CheckInReport,
    CheckOutReport,
    GuestReport,
    BookingSummaryReport,
    HotelWiseBookingReport,
    RoomTypePopularityReport,
    CouponUsageReport,
    OccupancyReport
  },
  data() {
    return {
      dailySummary: { checkins: [], checkouts: [],totalIns:0, totalOuts:0,sumAmountForCheckin:0 },
      occupancyReportData: [],
      revenue: [],
      guests: [],
      total_guests:0,
      total_bookings:0,
      hotelOptions: [
        { value: null, text: 'All Hotels' }
      ],
      statusOptions:[
        { value: null, text: 'All' }
      ],
      paymentStatusOptions:[
        { value: null, text: 'All' }
      ],
      occupancyFields: [
        { key: 'hotel_name', label: 'Hotel Name',sortable:true },
        { key: 'date', label: 'Date'},
        { key: 'room_type', label: 'Room Type'},
        { key: 'total_rooms', label: 'Total Rooms'},
        { key: 'occupied_rooms', label: 'Occupied Rooms',sortable:true },
        { key: 'occupancy_rate', label: 'Occupancy Rate (%)',sortable:true }
      ],
      revenueTable: [],
      revenueFields: [
        { key: 'date', label: 'Date' },
        { key: 'revenue_per_day', label: 'Revenue (Day)' },
        { key: 'revenue_per_room', label: 'Revenue per Room' },
        { key: 'revenue_per_type', label: 'Revenue per Type' }
      ],
      roomFields: [
        { key: 'room', label: 'Room' },
        { key: 'revenue', label: 'Revenue' }
      ],
      typeFields: [
        { key: 'room_type', label: 'Room Type' },
        { key: 'revenue', label: 'Revenue' }
      ],
      paymentStatuses: PAYMENT_STATUS_OPTIONS,
      freeBreakfast:false,
      activeTabIndex:0,
      reportTitle:'Booking & Revenue Reports',
    }
  },
  methods: {
    async fetchDailySummary(params) {
      const resp = await request({
          url: `/reports/daily-summary`,
          method: 'GET',
          params: params,
      });
      this.dailySummary = resp;
    },
    async fetchOccupancy(params) {
      const res = await request({
        url: `/reports/occupancy`,
        method: 'GET',
        params: params,
      })
      this.occupancyReportData = res.data;
    },
    async fetchRevenue() {
      const res = await axios.get('/reports/revenue', { params: { date: this.date } });
      // Transform API data for sub-tables
      const revenue_per_room = (res.data.revenue_per_room || []).map(r => ({
        room: r.room_name || r.room_id, // Use room name if available
        revenue: r.revenue
      }));
      const revenue_per_type = (res.data.revenue_per_type || []).map(t => ({
        room_type: t.room_type,
        revenue: t.revenue
      }));
      this.revenueTable = [{
        date: res.data.date,
        revenue_per_day: res.data.revenue_per_day,
        revenue_per_room,
        revenue_per_type
      }];
    },
    async fetchGuests(params) {
        try {
            const response = await axios.get('/reports/guests', { params });
            
            const {guests, total_guests, total_bookings} = response.data;
            this.guests = guests;
            this.total_guests = total_guests;
            this.total_bookings = total_bookings;
        } catch (err) {
            this.$bvToast.toast('Failed to load guests', { variant: 'danger', solid: true });
        }
    },
    async fetchFilterOptions() {
      try {
        const response = await request({
          method: 'get',
          url: '/hotel-reports/filter-options'
        });

        this.hotelOptions = [
          { value: null, text: 'All Hotels' },
          ...response.hotels.map(hotel => ({
            value: hotel.id,
            text: hotel.name
          }))
        ];
        this.statusOptions = [{ value: null, text: 'All' },...response.statuses];
        this.paymentStatusOptions = [{ value: null, text: 'All' },...response.payment_statuses];
        

      } catch (error) {
        console.error('Error fetching filter options:', error);
        this.$bvToast.toast('Failed to load filter options', {
          title: 'Error',
          variant: 'danger'
        });
      }
    },
  },
  mounted() {
    this.fetchFilterOptions();
    this.fetchOccupancy();
    this.fetchRevenue();
    this.fetchGuests();
    this.fetchDailySummary();
  },
  watch:{
    activeTabIndex(n){
      const titles = {
        0 :  "Booking Summary",
        1 :  "Hotel-wise Bookings",
        2 :  "Booking Revenue",
        3 :  "Room Popularity",
        4 :  "Coupon Usage",
        5 :  "Check Ins",
        6 :  "Guests and Pre-checkin",
        7 :  "Check Outs",
        8 :  "Occupancy",
        9 :  "Revenu"
      };
      this.reportTitle = titles[n] || "Report";
    }
  }
}
</script>

<style scoped>
.report-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #000;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.custom-tabs .nav-link.active {
  background: #2563eb !important;
  color: #fff !important;
  border-radius: 999px !important;
  font-weight: 600;
  box-shadow: 0 2px 8px #2563eb22;
}

.custom-tabs .nav-link {
  border-radius: 999px !important;
  margin-right: 0.5rem;
  color: #2563eb !important;
  background: #f3f4f6 !important;
  transition: background 0.2s, color 0.2s;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2563eb;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.date-picker {
  min-width: 220px;
  margin-left: 0.5rem;
}

.inner-table {
  background: #f9fafb;
  border-radius: 8px;
  box-shadow: 0 1px 4px #0001;
}

.badge {
  display: inline-block;
  padding: 0.25em 0.75em;
  font-size: 0.85em;
  font-weight: 600;
  border-radius: 999px;
  text-transform: capitalize;
  letter-spacing: 0.02em;
  margin-right: 0.25em;
}

.bg-success {
  background: #22c55e !important;
  color: #fff !important;
}

.bg-warning {
  background: #fbbf24 !important;
  color: #222 !important;
}

.bg-danger {
  background: #ef4444 !important;
  color: #fff !important;
}

.bg-info {
  background: #2563eb !important;
  color: #fff !important;
}

.bg-primary {
  background: #3b82f6 !important;
  color: #fff !important;
}

.bg-secondary {
  background: #6b7280 !important;
  color: #fff !important;
}

.text-white {
  color: #fff !important;
}

.text-dark {
  color: #222 !important;
}

@media (max-width: 768px) {
  .report-title {
    font-size: 1.1rem;
  }
}
</style>
